/**
 * `SiteSettlementRailSection` — per-SITE settlement-rail override (ADR-0096).
 * Mounted in `SettingsIntegrations.tsx` directly below the per-ACCOUNT
 * `SettlementRailSection`.
 *
 * The account control is the DEFAULT; this control is the OVERRIDE that wins
 * for THIS site. It lets one account route a Shopify site to Shopify and a
 * Stripe site to Stripe (the multi-rail-account case). Three options:
 *   - Inherit account default — clears the override (`rail: null`).
 *   - Stripe / Shopify — pins this site to that rail (must be connected).
 *
 * Role gate: owner/admin only (server 403 for reviewer/viewer; the section
 * renders nothing). Bootstrap GET on mount; PUT on change with an optimistic
 * flip + rollback + toast on failure (incl. the `rail_not_connected` 409).
 */

import { useCallback, useEffect, useState } from 'react';
import { CreditCard, ShoppingBag, Layers } from 'lucide-react';

import type { UserRole } from '@sill/types';

import { useOptionalHttp } from '../../api/http-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import type { ConnectedRails, SettlementRail } from '../../api/account-settlement-rail.js';
import {
  getSiteSettlementRail,
  putSiteSettlementRail,
  type SiteRailOverride,
} from '../../api/site-settlement-rail.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

export interface SiteSettlementRailSectionProps {
  readonly site_id: string;
  /** Optional role override for tests that don't wire `<AuthProvider />`. */
  readonly role_override?: UserRole;
}

type LoadState =
  | { kind: 'loading' }
  | {
      kind: 'ready';
      override: SiteRailOverride;
      account_default: SettlementRail;
      connected: ConnectedRails;
    }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/** The override choice as a stable radio value (`'inherit'` ↔ override `null`). */
type Choice = 'inherit' | SettlementRail;

function toChoice(o: SiteRailOverride): Choice {
  return o === null ? 'inherit' : o;
}
function toOverride(c: Choice): SiteRailOverride {
  return c === 'inherit' ? null : c;
}

export function SiteSettlementRailSection({
  site_id,
  role_override,
}: SiteSettlementRailSectionProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const auth = useOptionalAuth();
  const toast = useToast();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;

  const can_read = role === 'owner' || role === 'admin';
  const can_write = can_read;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [pending, setPending] = useState<Choice | null>(null);

  useEffect(() => {
    if (!can_read) {
      setState({ kind: 'forbidden' });
      return;
    }
    if (http === null) return;
    let cancelled = false;
    setState({ kind: 'loading' });
    void (async () => {
      const result = await getSiteSettlementRail(http, site_id);
      if (cancelled) return;
      switch (result.kind) {
        case 'ok':
          setState({
            kind: 'ready',
            override: result.override,
            account_default: result.account_default,
            connected: result.connected,
          });
          break;
        case 'forbidden':
          setState({ kind: 'forbidden' });
          break;
        case 'not_found':
          setState({ kind: 'error', message: 'Site not found.' });
          break;
        case 'error':
          setState({ kind: 'error', message: result.message });
          break;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [http, can_read, site_id]);

  const onChoose = useCallback(
    async (choice: Choice): Promise<void> => {
      if (!can_write || state.kind !== 'ready' || http === null) return;
      if (toChoice(state.override) === choice) return;
      // UI half of the connected-rail gate (clearing → inherit is always OK).
      if (choice !== 'inherit' && !state.connected[choice]) return;
      const previous = state.override;
      const account_default = state.account_default;
      const connected = state.connected;
      const next = toOverride(choice);
      setPending(choice);
      setState({ kind: 'ready', override: next, account_default, connected });
      const result = await putSiteSettlementRail(http, site_id, next);
      setPending(null);
      const rollback = (): void =>
        setState({ kind: 'ready', override: previous, account_default, connected });
      switch (result.kind) {
        case 'ok':
          setState({ kind: 'ready', override: result.override, account_default, connected });
          toast({
            tone: 'lime',
            title: 'Site rail saved.',
            body:
              result.override === null
                ? 'This site now inherits the account default.'
                : 'This site now settles through ' + result.override + '.',
          });
          break;
        case 'rail_not_connected':
          rollback();
          toast({ tone: 'coral', title: 'That rail is not connected.', body: 'Connect it above first.' });
          break;
        case 'not_found':
          rollback();
          toast({ tone: 'coral', title: 'Site not found.' });
          break;
        case 'forbidden':
          rollback();
          toast({ tone: 'coral', title: 'Your role cannot change this setting.' });
          break;
        case 'bad_request':
          rollback();
          toast({ tone: 'coral', title: 'That value is not a supported rail.' });
          break;
        case 'error':
          rollback();
          toast({ tone: 'coral', title: 'Could not save site rail.', body: result.message });
          break;
      }
    },
    [can_write, http, state, site_id, toast],
  );

  if (!can_read) return null;

  const account_default = state.kind === 'ready' ? state.account_default : null;
  const options: readonly { value: Choice; label: string; hint: string; icon: typeof CreditCard }[] = [
    {
      value: 'inherit',
      label: 'Inherit account default',
      hint:
        account_default !== null
          ? `Use the account-level rail (currently ${account_default}).`
          : 'Use the account-level rail.',
      icon: Layers,
    },
    { value: 'stripe', label: 'Stripe', hint: 'Pin this site to your connected Stripe account.', icon: CreditCard },
    { value: 'shopify', label: 'Shopify', hint: 'Pin this site to your connected Shopify store.', icon: ShoppingBag },
  ];

  return (
    <Card className="p-5 sm:p-6">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>
          THIS SITE&apos;S RAIL
        </div>
        <div style={{ fontFamily: serifFont, fontSize: 22, color: C.text, letterSpacing: -0.5, marginBottom: 4 }}>
          Override the settlement rail for this site
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
          By default this site uses the account rail above. Override it here when
          one account owns sites on different rails — e.g. a Shopify store and a
          Stripe site. Only a connected rail can be selected.
        </div>
      </div>

      {state.kind === 'loading' ? (
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
          Reading site rail…
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="mt-2" style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, lineHeight: 1.5 }}>
          Couldn&apos;t load site rail — {state.message}
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <fieldset
          aria-label="Site settlement rail"
          className="space-y-2"
          style={{ border: 'none', padding: 0, margin: 0 }}
        >
          {options.map((opt) => {
            const checked = toChoice(state.override) === opt.value;
            const is_connected = opt.value === 'inherit' ? true : state.connected[opt.value];
            const is_pending = pending === opt.value;
            const disabled = !can_write || pending !== null || !is_connected;
            const Icon = opt.icon;
            return (
              <label
                key={opt.value}
                className="flex items-start gap-3 p-3"
                style={{
                  background: checked ? C.surface2 : 'transparent',
                  border: `1px solid ${checked ? C.lime : C.border}`,
                  borderRadius: 2,
                  opacity: (pending !== null && !is_pending) || !is_connected ? 0.6 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="site_settlement_rail"
                  value={opt.value}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => void onChoose(opt.value)}
                  className="mt-0.5"
                  style={{ accentColor: C.lime }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Icon size={14} color={C.text} />
                    <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
                      {opt.label}
                    </div>
                    {opt.value !== 'inherit' && !is_connected ? (
                      <span
                        style={{
                          fontFamily: monoFont, fontSize: 9, color: C.muted, letterSpacing: 1,
                          border: `1px solid ${C.border}`, borderRadius: 2, padding: '1px 5px',
                        }}
                      >
                        NOT CONNECTED
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
                    {opt.hint}
                  </div>
                </div>
              </label>
            );
          })}
        </fieldset>
      ) : null}
    </Card>
  );
}
